import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Check, X, Trash2, Edit2, RotateCcw, CalendarClock, User } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

// To-do personali. modalita="staff" (dashboard): schede secondo le viste consentite dal server
// (miei / creati da me / direttivo / tutti per ROOT), crea/modifica/elimina. modalita="miei":
// solo i compiti assegnati all'utente (portale allenatore). Si spunta solo ciò che è assegnato a sé.
const oggi = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const fmtData = (d) => new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
const VUOTO = { titolo: '', descrizione: '', scadenza: '', assegnato_a_id: '' };
const ETICHETTE = { miei: 'Assegnati a me', creati: 'Creati da me', direttivo: 'Direttivo', tutti: 'Tutti' };

export default function TodoList({ modalita = 'staff' }) {
  const { user } = useAuth();
  const staff = modalita === 'staff';
  const [compiti, setCompiti] = useState([]);
  const [assegnatari, setAssegnatari] = useState([]);
  const [vista, setVista] = useState('miei');
  const [viste, setViste] = useState(['miei']);
  const [fatti, setFatti] = useState(false);
  const [form, setForm] = useState(null);       // null = chiuso; { ...VUOTO, id? }
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      let r;
      const completati = fatti ? 1 : undefined;
      if (!staff) r = await api.get('/compiti/miei', { params: { completati } });
      else r = await api.get('/compiti', { params: { vista, completati } });
      setCompiti(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [staff, vista, fatti]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!staff) return;
    api.get('/compiti/assegnatari').then(r => setAssegnatari(r.data)).catch(() => {});
    api.get('/compiti/viste').then(r => setViste(r.data)).catch(() => {});
  }, [staff]);

  const salva = async (e) => {
    e.preventDefault();
    const body = { ...form, assegnato_a_id: form.assegnato_a_id || null, scadenza: form.scadenza || null };
    try {
      if (form.id) await api.put(`/compiti/${form.id}`, body);
      else await api.post('/compiti', body);
      setForm(null); load();
    } catch (err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };
  const spunta = async (c) => {
    try { await api.patch(`/compiti/${c.id}/fatto`, { completato: !c.completato }); load(); }
    catch (err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };
  const elimina = async (c) => {
    if (!window.confirm(`Eliminare "${c.titolo}"?`)) return;
    try { await api.delete(`/compiti/${c.id}`); load(); }
    catch (err) { alert('Errore: ' + (err.response?.data?.error || err.message)); }
  };
  const puoModificare = (c) => user?.ruolo === 'ROOT' || c.creato_da?.id === user?.id;
  // Si segna come fatto solo ciò che è assegnato a sé (o creato da sé, se non assegnato a nessuno)
  const puoSpuntare = (c) => (c.assegnato_a?.id ?? c.creato_da?.id) === user?.id;

  if (!staff && !loading && compiti.length === 0) return null; // allenatore senza compiti: niente riquadro

  return (
    <div style={s.card}>
      <div style={s.head}>
        <div style={s.title}>{staff ? 'DA FARE' : 'I MIEI COMPITI'}</div>
        {staff && (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {viste.map(k => (
              <button key={k} style={{ ...s.pill, ...(vista === k ? s.pillOn : {}) }} onClick={() => setVista(k)}>{ETICHETTE[k]}</button>
            ))}
            <button style={{ ...s.pill, ...(fatti ? s.pillOn : {}) }} onClick={() => setFatti(f => !f)} title="Mostra i completati">✓ Fatti</button>
            <button style={s.btnPri} onClick={() => setForm({ ...VUOTO, assegnato_a_id: user?.id || '' })}><Plus size={13} /> Nuovo</button>
          </div>
        )}
      </div>

      {form && (
        <form onSubmit={salva} style={s.form}>
          <input style={{ ...s.inp, gridColumn: '1 / -1' }} placeholder="Cosa c'è da fare?" value={form.titolo} autoFocus
            onChange={e => setForm(p => ({ ...p, titolo: e.target.value }))} />
          <select style={s.inp} value={form.assegnato_a_id || ''} onChange={e => setForm(p => ({ ...p, assegnato_a_id: e.target.value }))}>
            <option value="">— nessuno —</option>
            {assegnatari.map(a => <option key={a.id} value={a.id}>{a.nome}{a.ruolo === 'ALLENATORE' ? ' (allenatore)' : ''}{a.is_direttivo ? ' · direttivo' : ''}</option>)}
          </select>
          <input type="date" style={s.inp} value={form.scadenza || ''} onChange={e => setForm(p => ({ ...p, scadenza: e.target.value }))} />
          <textarea style={{ ...s.inp, gridColumn: '1 / -1', minHeight: '54px', resize: 'vertical' }} placeholder="Dettagli (facoltativo)"
            value={form.descrizione || ''} onChange={e => setForm(p => ({ ...p, descrizione: e.target.value }))} />
          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
            <button type="button" style={s.btnSec} onClick={() => setForm(null)}><X size={13} /> Annulla</button>
            <button type="submit" style={s.btnPri} disabled={!form.titolo.trim()}><Check size={13} /> Salva</button>
          </div>
        </form>
      )}

      {loading ? <div style={s.empty}>Caricamento...</div> : compiti.length === 0 ? (
        <div style={s.empty}>{fatti ? 'Ancora nessun compito completato' : 'Niente da fare qui 🎉'}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {compiti.map(c => {
            const scaduto = !c.completato && c.scadenza && new Date(c.scadenza) < oggi();
            return (
              <div key={c.id} style={{ ...s.row, opacity: c.completato ? 0.6 : 1 }}>
                <button style={{ ...s.check, ...(c.completato ? s.checkOn : {}), ...(puoSpuntare(c) ? {} : s.checkOff) }} disabled={!puoSpuntare(c)}
                  onClick={() => spunta(c)} title={!puoSpuntare(c) ? `Lo segna come fatto ${c.assegnato_a?.nome || 'chi l\'ha assegnato'}` : c.completato ? 'Riapri' : 'Segna come fatto'}>
                  {c.completato && <Check size={12} color="#fff" />}
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, textDecoration: c.completato ? 'line-through' : 'none' }}>{c.titolo}</div>
                  {c.descrizione && <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted,#888)', whiteSpace: 'pre-wrap' }}>{c.descrizione}</div>}
                  <div style={s.meta}>
                    {c.assegnato_a && <span style={s.metaItem}><User size={11} /> {c.assegnato_a.nome}</span>}
                    {c.scadenza && <span style={{ ...s.metaItem, color: scaduto ? '#EF4444' : undefined, fontWeight: scaduto ? 700 : 400 }}><CalendarClock size={11} /> {fmtData(c.scadenza)}{scaduto ? ' · scaduto' : ''}</span>}
                    {staff && c.creato_da && c.creato_da.id !== c.assegnato_a?.id && <span style={s.metaItem}>da {c.creato_da.nome}</span>}
                    {c.completato && <span style={s.metaItem}>fatto {c.completato_da ? `da ${c.completato_da} ` : ''}il {fmtData(c.completato_at)}</span>}
                  </div>
                </div>
                {staff && puoModificare(c) && !c.completato && (
                  <button style={s.icon} title="Modifica" onClick={() => setForm({
                    id: c.id, titolo: c.titolo, descrizione: c.descrizione || '', assegnato_a_id: c.assegnato_a?.id || '',
                    scadenza: c.scadenza ? c.scadenza.slice(0, 10) : '',
                  })}><Edit2 size={12} /></button>
                )}
                {c.completato && puoSpuntare(c) && <button style={s.icon} title="Riapri" onClick={() => spunta(c)}><RotateCcw size={12} /></button>}
                {staff && puoModificare(c) && <button style={{ ...s.icon, color: '#EF4444' }} title="Elimina" onClick={() => elimina(c)}><Trash2 size={12} /></button>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const s = {
  card:    { background: 'var(--bg-card)', border: '1px solid var(--border,#1E1E1E)', borderRadius: '12px', padding: '18px 20px' },
  head:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', gap: '10px', flexWrap: 'wrap' },
  title:   { fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: '2px', color: 'var(--text-muted)' },
  pill:    { background: 'transparent', border: '1px solid var(--border2,#2A2A2A)', borderRadius: '99px', padding: '4px 10px', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted,#888)', cursor: 'pointer' },
  pillOn:  { background: '#F4600C20', borderColor: '#F4600C60', color: '#F4600C', fontWeight: 700 },
  btnPri:  { display: 'flex', alignItems: 'center', gap: '5px', background: '#F4600C', color: '#fff', border: 'none', borderRadius: '8px', padding: '6px 11px', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' },
  btnSec:  { display: 'flex', alignItems: 'center', gap: '5px', background: 'var(--bg-input,#1A1A1A)', color: 'var(--text,#fff)', border: '1px solid var(--border2,#2A2A2A)', borderRadius: '8px', padding: '6px 11px', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' },
  form:    { display: 'grid', gridTemplateColumns: '1fr 160px', gap: '8px', padding: '12px', marginBottom: '12px', background: 'var(--bg-input,#1A1A1A)', borderRadius: '10px', border: '1px solid #F4600C40' },
  inp:     { background: 'var(--bg-card,#141414)', border: '1px solid var(--border2,#2A2A2A)', borderRadius: '8px', padding: '8px 10px', color: 'var(--text,#fff)', fontSize: 'var(--font-size-sm)', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' },
  row:     { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '9px 10px', background: 'var(--bg-input,#1A1A1A)', borderRadius: '8px' },
  check:   { width: '18px', height: '18px', borderRadius: '5px', border: '1.5px solid #555', background: 'transparent', cursor: 'pointer', flexShrink: 0, marginTop: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
  checkOn: { background: '#22C55E', borderColor: '#22C55E' },
  checkOff:{ cursor: 'not-allowed', opacity: 0.35 },
  meta:    { display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '3px' },
  metaItem:{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: 'var(--font-size-xs)', color: 'var(--text-dim,#666)' },
  icon:    { background: 'transparent', border: 'none', color: 'var(--text-dim,#666)', cursor: 'pointer', padding: '3px', display: 'flex' },
  empty:   { color: '#555', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: '24px 0' },
};
